import { formatEther, parseEther } from 'viem';

import { AddressString, TCommunityIssuance, TStabilityPool } from '~components/providers/types';
import { getCollateralConfig } from '~config/protocol';
import { NetworkIds } from '~config/rpcConfig';
import { EVestaAsset } from '~config/types';
import VestaSubgraph from '~services/subgraph/vesta';
import { IStabilityPoolData, IStabilityPoolUserData } from '~services/types';
import { makeThrowable } from '~utils/error/errorHandlers';
import { ERROR_MSGS } from '~utils/error/errors';

export async function getGraphSystemStabilityPool({
  asset,
  spAddress,
  communityIssuanceRead,
  vestaSubgraph,
}: {
  asset: EVestaAsset;
  spAddress: AddressString;
  communityIssuanceRead: TCommunityIssuance['read'];
  vestaSubgraph: VestaSubgraph;
}): Promise<IStabilityPoolData> {
  try {
    const [totalVstDeposit, rewards] = await Promise.all([
      vestaSubgraph.stabilityPool.getStabilityPoolTotalDeposits(asset),
      communityIssuanceRead.stabilityPoolRewards([spAddress]),
    ]);

    return {
      totalVSTDeposits: Number(totalVstDeposit),
      rewardInfo: {
        rewardToken: rewards[0],
        totalRewardIssued: Number(formatEther(rewards[1])),
        lastUpdateTime: Number(formatEther(rewards[2])),
        totalRewardSupply: Number(formatEther(rewards[3])),
        rewardDistribution: Number(formatEther(rewards[4])),
      },
    };
  } catch (err) {
    console.error(err);
    throw makeThrowable(ERROR_MSGS.CT_FETCH_SYS_SP_FAIL);
  }
}

export async function getGraphUserStabilityPool({
  userAddress,
  asset,
  vestaSubgraph,
}: {
  userAddress: string;
  asset: EVestaAsset;
  vestaSubgraph: VestaSubgraph;
}): Promise<IStabilityPoolUserData> {
  try {
    const { rewardGains, assetGains, userDeposit } =
      await vestaSubgraph.stabilityPool.getUserStabilityPoolRewards({
        asset,
        user: userAddress,
      });

    return {
      vstDeposit: Number(userDeposit),
      rawVstDeposit: parseEther(userDeposit),
      liquidationGain: assetGains,
      tokenRewards: rewardGains,
    };
  } catch (err) {
    console.error(err);
    throw makeThrowable(ERROR_MSGS.CT_FETCH_USER_SP_FAIL);
  }
}

export async function getChainSystemStabilityPool(
  asset: EVestaAsset,
  network: NetworkIds,
  stabilityPoolRead: TStabilityPool['read'],
  communityIssuanceRead: TCommunityIssuance['read'],
): Promise<IStabilityPoolData> {
  const spAddress = getCollateralConfig(asset).getStabilityPoolAddress(network);

  try {
    const [totalVstDeposit, rewards] = await Promise.all([
      stabilityPoolRead.getTotalVSTDeposits(),
      communityIssuanceRead.stabilityPoolRewards([spAddress]),
    ]);

    return {
      totalVSTDeposits: Number(formatEther(totalVstDeposit)),
      rewardInfo: {
        rewardToken: rewards[0],
        totalRewardIssued: Number(formatEther(rewards[1])),
        lastUpdateTime: Number(formatEther(rewards[2])),
        totalRewardSupply: Number(formatEther(rewards[3])),
        rewardDistribution: Number(formatEther(rewards[4])),
      },
    };
  } catch (err) {
    throw makeThrowable(ERROR_MSGS.CT_FETCH_SYS_SP_FAIL);
  }
}

export async function getChainUserStabilityPool(
  userAddress: AddressString,
  stabilityPoolRead: TStabilityPool['read'],
): Promise<IStabilityPoolUserData> {
  try {
    const [depositorTokenGain, depositorVstaGain, compoundedVstDeposit] = await Promise.all([
      stabilityPoolRead.getDepositorAssetGain([userAddress]),
      stabilityPoolRead.getDepositorVSTAGain([userAddress]),
      stabilityPoolRead.getCompoundedVSTDeposit([userAddress]),
    ]);

    return {
      tokenRewards: Number(formatEther(depositorVstaGain)),
      liquidationGain: Number(formatEther(depositorTokenGain)),
      vstDeposit: Number(formatEther(compoundedVstDeposit)),
      rawVstDeposit: compoundedVstDeposit,
    };
  } catch (err) {
    throw makeThrowable(ERROR_MSGS.CT_FETCH_USER_SP_FAIL);
  }
}

export const calcUserRewardGain = ({
  globalG,
  userG,
  userP,
  userDeposit,
}: {
  globalG: string;
  userG: string;
  userP: string;
  userDeposit: string;
}) => {
  const gain = BigInt(((Number(globalG) - Number(userG)) / Number(userP)) * Number(userDeposit) || 0);
  return Number(formatEther(gain));
};

export const calcUserAssetGain = ({
  globalS,
  userS,
  userP,
  userDeposit,
}: {
  globalS: string;
  userS: string;
  userP: string;
  userDeposit: string;
}) => {
  const gain = BigInt(Number(globalS) - (Number(userS) / Number(userP)) * Number(userDeposit) || 0);
  return Number(formatEther(gain));
};
